import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    MenuItem,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    Container,
    Stack,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Snackbar,
    Select
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SubjectIcon from '@mui/icons-material/School';
import GradeIcon from '@mui/icons-material/Grade';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SpeedIcon from '@mui/icons-material/Speed';
import DownloadIcon from '@mui/icons-material/Download';
import GenerateIcon from '@mui/icons-material/AutoFixHigh';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NoteIcon from '@mui/icons-material/Note';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';


//import axios from 'axios';
import api from '../axios/axiosInstance';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType } from "docx";
import AIGenerationLimitDialog from '../dialogs/AIGenerationLimitDialog';
import handleAIError from '../utils/handleAIError';
import useSubjectsAndUnits from '../hooks/useSubjectsAndUnits';
import { SpinningGradelyLoader, FloatingGradelyLoader } from '../loadingIcons/LoadingIcons';

const DOK_OPTIONS = [1, 2, 3, 4];
const GRADES = ['KG', 1, 2, 3, 4, 5, 6, 7, 8];

const RubricGenerator = ({ setActiveTabGlobal }) => {
    const [gradeLevel, setGradeLevel] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [grade, setGrade] = useState('');

    const [topic, setTopic] = useState('');
    const [dokLevels, setDokLevels] = useState([]);
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const rubricRef = useRef();

    //Dialogue Box for AI Limit
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [alertDialog, setAlertDialog] = useState({ open: false, message: "" });


    // Removed subject/unit hooks since we're using text input now
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleDOKToggle = (level) => {
        setDokLevels((prev) =>
            prev.includes(level)
                ? prev.filter((l) => l !== level)
                : [...prev, level]
        );
    };

    const handleSubmit = async () => {
        if (!selectedSubject || !grade || !topic || dokLevels.length === 0) {
            alert('Please fill all fields and select at least one DOK level.');
            return;
        }

        setLoading(true);
        setError('');
        try {

            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/generate-rubric/`,
                {
                    grade_level: String(grade),
                    subject: selectedSubject,
                    topic,
                    dok_levels: dokLevels
                },
                { withCredentials: true }
            );
            setResponse(res.data);

        } catch (err) {

            handleAIError(err, { setErrorMsg: (msg) => setAlertDialog({ open: true, message: msg }), setShowLimitDialog });
        } finally {
            setLoading(false);
        }
    };


    const generatePDFHTML = (rubricData) => {
        // Theme colors for PDF
        const primaryColor = '#546A2F'; // Theme main color
        const secondaryColor = '#A8B87C'; // Theme secondary color
        const successColor = '#2e7d32'; // Material-UI success green
        const infoColor = '#0288d1'; // Material-UI info blue
        const warningColor = '#ed6c02'; // Material-UI warning orange
        const errorColor = '#d32f2f'; // Material-UI error red
        const greyColor = '#757575'; // Material-UI grey
        const lightGreyColor = '#f5f5f5'; // Light grey background
        const borderColor = '#e0e0e0'; // Light border color

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Assessment Rubric - ${selectedSubject || 'Subject'}</title>
            </head>
            <body style="font-family: 'Times New Roman', serif; margin: 0; padding: 0; line-height: 1.5; color: #212121; font-size: 11pt; background: white;">
                <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid ${primaryColor}; padding-bottom: 15px;">
                    <div style="font-size: 20pt; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; color: ${primaryColor};">Assessment Rubric</div>
                    <div style="font-size: 12pt; font-weight: bold; margin-bottom: 5px; color: ${secondaryColor};">${selectedSubject || 'Subject'} | Grade ${grade}</div>
                </div>

                <div style="background: ${lightGreyColor}; border: 2px solid ${primaryColor}; padding: 15px; margin-bottom: 25px; border-radius: 8px;">
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Subject:</span>
                            <span style="font-weight: bold; color: #212121; font-size: 10pt;">${selectedSubject || 'N/A'}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Grade:</span>
                            <span style="font-weight: bold; color: #212121; font-size: 10pt;">${grade}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid ${borderColor};">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">Topic:</span>
                            <span style="font-weight: bold; color: #212121; font-size: 10pt;">${topic}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px 0;">
                            <span style="font-weight: bold; color: ${primaryColor}; font-size: 10pt; text-transform: uppercase;">DOK Levels:</span>
                            <span style="font-weight: bold; color: ${successColor}; font-size: 10pt;">${dokLevels.sort((a, b) => a - b).join(', ')}</span>
                        </div>
                    </div>
                </div>

                ${Object.entries(rubricData).filter(([key]) => key !== 'rubric_rows').map(([key, value]) => `
                    <div style="margin-bottom: 25px; page-break-inside: avoid;">
                        <div style="font-size: 14pt; font-weight: bold; color: ${primaryColor}; margin-bottom: 12px; padding-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                        <div style="background: white; border: 2px solid ${borderColor}; padding: 15px; margin-bottom: 15px; border-radius: 8px;">
                            ${typeof value === 'string' ?
                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${value}</div>` :
                Array.isArray(value) ?
                    `<div>${value.map((item, index) => `<div style="margin-bottom: 8px; padding-left: 25px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                    typeof value === 'object' && value !== null ?
                        Object.entries(value).map(([subKey, subValue]) => `
                                    <div style="margin-bottom: 15px;">
                                        <div style="font-weight: bold; color: ${secondaryColor}; margin-bottom: 6px; font-size: 10pt; text-transform: uppercase;">${subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</div>
                                        ${Array.isArray(subValue) ?
                                `<div>${subValue.map((item, index) => `<div style="margin-bottom: 8px; padding-left: 25px; position: relative; text-align: justify; font-size: 10pt;"><span style="position: absolute; left: 0; font-weight: bold; color: ${primaryColor}; font-size: 11pt;">${index + 1}. </span><span style="color: #212121;">${item}</span></div>`).join('')}</div>` :
                                `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${subValue}</div>`
                            }
                                    </div>
                                `).join('') :
                        `<div style="font-size: 10pt; line-height: 1.4; text-align: justify; margin-bottom: 10px; color: #212121;">${String(value)}</div>`
            }
                        </div>
                    </div>
                `).join('')}

                ${rubricData.rubric_rows ? `
                <div style="margin-bottom: 25px; page-break-inside: avoid;">
                    <div style="font-size: 14pt; font-weight: bold; color: ${primaryColor}; margin-bottom: 12px; padding-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Assessment Rubric</div>
                    <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 9pt; border: 2px solid ${borderColor}; border-radius: 8px; overflow: hidden;">
                            <thead>
                                <tr style="background: ${primaryColor};">
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: center; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">DOK</th>
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">Learning Outcome</th>
                                
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">Emerging</th>
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">Developing</th>
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">Proficient</th>
                                    <th style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 10pt; text-transform: uppercase; color: white;">Extending</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${rubricData.rubric_rows.map((row, index) => `
                                    <tr style="background: ${index % 2 === 0 ? '#ffffff' : lightGreyColor};">
                                       
                                        <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: center; vertical-align: top; font-size: 9pt; color: ${successColor}; font-weight: bold;">${row.dok}</td>
                                         <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-weight: bold; font-size: 9pt; color: #212121;">${row.learning_outcome}</td>
                                        <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-size: 9pt; line-height: 1.4; color: #212121;">${row.emerging}</td>
                                        <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-size: 9pt; line-height: 1.4; color: #212121;">${row.developing}</td>
                                        <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-size: 9pt; line-height: 1.4; color: #212121;">${row.proficient}</td>
                                        <td style="border: 1px solid ${borderColor}; padding: 10px; text-align: left; vertical-align: top; font-size: 9pt; line-height: 1.4; color: #212121;">${row.extending}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                </div>
                ` : ''}

                <div style="background: ${lightGreyColor}; border: 2px solid ${warningColor}; padding: 15px; margin-top: 20px; border-radius: 8px;">
                    <div style="font-size: 12pt; font-weight: bold; margin-bottom: 10px; color: ${warningColor}; text-transform: uppercase; padding-bottom: 3px;">Assessment Notes</div>
                    <div style="text-align: justify; font-size: 10pt; line-height: 1.4; color: #212121;">
                        This rubric is designed to assess student performance based on curriculum standards. 
                        Teachers should use this rubric consistently and provide constructive feedback to support student learning.
                    </div>
                </div>
            </body>
            </html>
        `;
        return html;
    };

    const handleDownloadPDF = () => {
        if (!response) return;

        const pdfHTML = generatePDFHTML(response);
        const blob = new Blob([pdfHTML], { type: 'text/html' });
        const url = URL.createObjectURL(blob);

        const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `Rubic Generator - ${selectedSubject || 'Subject'}.pdf`, //`rubric_${selectedSubject?.subject_name || 'subject'}_${topic.replace(/\s+/g, '_')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                scrollX: 0,
                scrollY: 0
            },
            jsPDF: {
                unit: 'in',
                format: 'a4',
                orientation: 'portrait',
                compress: true
            },
            pagebreak: { mode: 'avoid-all' }
        };

        // Create a temporary iframe to render the HTML
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '800px';
        iframe.style.height = '600px';
        document.body.appendChild(iframe);

        iframe.onload = () => {
            setTimeout(() => {
                html2pdf().set(opt).from(iframe.contentDocument.body).save().then(() => {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                });
            }, 500);
        };

        iframe.src = url;
    };

    const generateWordDocument = async (rubricData) => {
        // Theme colors for Word document (matching PDF colors)
        const primaryColor = '546A2F'; // Theme main color
        const secondaryColor = 'A8B87C'; // Theme secondary color
        const successColor = '2e7d32'; // Material-UI success green
        const infoColor = '0288d1'; // Material-UI info blue
        const warningColor = 'ed6c02'; // Material-UI warning orange
        const errorColor = 'd32f2f'; // Material-UI error red

        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        // Title
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "ASSESSMENT RUBRIC",
                                    bold: true,
                                    size: 40,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.TITLE,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 300 }
                        }),

                        // Subject and Grade Information
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `${selectedSubject || 'Subject'} | Grade ${grade}`,
                                    bold: true,
                                    size: 24,
                                    color: secondaryColor
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 400 }
                        }),

                        // Assessment Information Section
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "ASSESSMENT INFORMATION",
                                    bold: true,
                                    size: 28,
                                    color: primaryColor
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: { before: 300, after: 200 }
                        }),

                        // Subject
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Subject: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: selectedSubject || 'N/A',
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Grade
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Grade: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: grade,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // Topic
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Topic: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: topic,
                                    size: 22
                                })
                            ],
                            spacing: { after: 100 }
                        }),

                        // DOK Levels
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "DOK Levels: ",
                                    bold: true,
                                    size: 22,
                                    color: primaryColor
                                }),
                                new TextRun({
                                    text: dokLevels.sort((a, b) => a - b).join(', '),
                                    size: 22,
                                    color: successColor
                                })
                            ],
                            spacing: { after: 400 }
                        }),

                        // Rubric Content Sections (excluding rubric_rows)
                        ...Object.entries(rubricData)
                            .filter(([key]) => key !== 'rubric_rows')
                            .flatMap(([key, value]) => [
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                            bold: true,
                                            size: 28,
                                            color: primaryColor
                                        })
                                    ],
                                    heading: HeadingLevel.HEADING_1,
                                    spacing: { before: 300, after: 200 }
                                }),

                                // Handle different value types
                                ...(typeof value === 'string' ? [
                                    new Paragraph({
                                        children: [
                                            new TextRun({
                                                text: value,
                                                size: 22
                                            })
                                        ],
                                        spacing: { after: 300 }
                                    })
                                ] : Array.isArray(value) ?
                                    value.map((item, index) =>
                                        new Paragraph({
                                            children: [
                                                new TextRun({
                                                    text: `${index + 1}. `,
                                                    bold: true,
                                                    size: 22,
                                                    color: primaryColor
                                                }),
                                                new TextRun({
                                                    text: item,
                                                    size: 22
                                                })
                                            ],
                                            spacing: { after: 100 }
                                        })
                                    ) :
                                    typeof value === 'object' && value !== null ?
                                        Object.entries(value).flatMap(([subKey, subValue]) => [
                                            new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) + ":",
                                                        bold: true,
                                                        size: 24,
                                                        color: secondaryColor
                                                    })
                                                ],
                                                spacing: { before: 200, after: 100 }
                                            }),
                                            ...(Array.isArray(subValue) ?
                                                subValue.map((item, index) =>
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: `${index + 1}. `,
                                                                bold: true,
                                                                size: 22,
                                                                color: primaryColor
                                                            }),
                                                            new TextRun({
                                                                text: item,
                                                                size: 22
                                                            })
                                                        ],
                                                        spacing: { after: 100 }
                                                    })
                                                ) : [
                                                    new Paragraph({
                                                        children: [
                                                            new TextRun({
                                                                text: String(subValue),
                                                                size: 22
                                                            })
                                                        ],
                                                        spacing: { after: 100 }
                                                    })
                                                ]
                                            )
                                        ]) : [
                                            new Paragraph({
                                                children: [
                                                    new TextRun({
                                                        text: String(value),
                                                        size: 22
                                                    })
                                                ],
                                                spacing: { after: 300 }
                                            })
                                        ]
                                )
                            ]),

                        // Assessment Rubric Table
                        ...(rubricData.rubric_rows ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "ASSESSMENT RUBRIC",
                                        bold: true,
                                        size: 28,
                                        color: primaryColor
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            new DocxTable({
                                width: {
                                    size: 100,
                                    type: WidthType.PERCENTAGE,
                                },
                                rows: [
                                    // Header row
                                    new DocxTableRow({
                                        children: [
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "DOK", bold: true, size: 20 })]
                                                })],
                                                width: { size: 10, type: WidthType.PERCENTAGE }
                                            }),
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "Learning Outcome", bold: true, size: 20 })]
                                                })],
                                                width: { size: 25, type: WidthType.PERCENTAGE }
                                            }),
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "Emerging", bold: true, size: 20, color: errorColor })]
                                                })],
                                                width: { size: 16.25, type: WidthType.PERCENTAGE }
                                            }),
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "Developing", bold: true, size: 20, color: warningColor })]
                                                })],
                                                width: { size: 16.25, type: WidthType.PERCENTAGE }
                                            }),
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "Proficient", bold: true, size: 20, color: successColor })]
                                                })],
                                                width: { size: 16.25, type: WidthType.PERCENTAGE }
                                            }),
                                            new DocxTableCell({
                                                children: [new Paragraph({
                                                    children: [new TextRun({ text: "Extending", bold: true, size: 20, color: infoColor })]
                                                })],
                                                width: { size: 16.25, type: WidthType.PERCENTAGE }
                                            })
                                        ]
                                    }),
                                    // Data rows
                                    ...rubricData.rubric_rows.map(row =>
                                        new DocxTableRow({
                                            children: [
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: String(row.dok || 'N/A'), size: 18, bold: true, color: successColor })]
                                                    })]
                                                }),
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: row.learning_outcome || 'N/A', size: 18, bold: true })]
                                                    })]
                                                }),
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: row.emerging || 'N/A', size: 18 })]
                                                    })]
                                                }),
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: row.developing || 'N/A', size: 18 })]
                                                    })]
                                                }),
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: row.proficient || 'N/A', size: 18 })]
                                                    })]
                                                }),
                                                new DocxTableCell({
                                                    children: [new Paragraph({
                                                        children: [new TextRun({ text: row.extending || 'N/A', size: 18 })]
                                                    })]
                                                })
                                            ]
                                        })
                                    )
                                ]
                            })
                        ] : []),

                        // Assessment Notes
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "ASSESSMENT NOTES",
                                    bold: true,
                                    size: 28,
                                    color: warningColor
                                })
                            ],
                            heading: HeadingLevel.HEADING_1,
                            spacing: { before: 300, after: 200 }
                        }),
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "This rubric is designed to assess student performance based on curriculum standards. Teachers should use this rubric consistently and provide constructive feedback to support student learning.",
                                    size: 22
                                })
                            ],
                            spacing: { after: 300 }
                        }),

                        // Footer
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Generated by Gradely Educational Platform",
                                    size: 18,
                                    italics: true,
                                    color: "757575"
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 600 }
                        })
                    ]
                }
            ]
        });

        return doc;
    };

    const downloadWordDocument = async (rubricData) => {
        try {
            const doc = await generateWordDocument(rubricData);
            const blob = await Packer.toBlob(doc);

            const element = document.createElement('a');
            element.href = URL.createObjectURL(blob);
            element.download = `Assessment_Rubric_${selectedSubject || 'Subject'}_${topic.replace(/\s+/g, '_')}.docx`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            URL.revokeObjectURL(element.href);
        } catch (error) {
            console.error('Error generating Word document:', error);
            alert('Error generating Word document. Please try again.');
        }
    };

    const renderRubric = (rubricData) => {
        if (!rubricData) return null;

        return (
            <Fade in timeout={600}>
                <Box ref={rubricRef}>
                    <Typography
                        variant={isSmallMobile ? "h6" : "h5"}
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                            textAlign: 'center',
                            color: theme.palette.primary.main,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1
                        }}
                    >
                        <AssessmentIcon fontSize="small" />
                        Generated Rubric
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {/* Basic Information */}
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2, sm: 3 },
                            mb: 4,
                            borderRadius: { xs: 1, sm: 1.5, md: 2 },
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            gutterBottom
                            sx={{
                                color: theme.palette.primary.main,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <AssignmentIcon fontSize="small" />
                            Assessment Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SubjectIcon fontSize="small" color="primary" />
                                    <strong>Subject:</strong> {selectedSubject}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GradeIcon fontSize="small" color="primary" />
                                    <strong>Grade:</strong> {grade}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmojiObjectsIcon fontSize="small" color="primary" />
                                    <strong>Topic:</strong> {topic}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Rubric Table */}
                    <Paper
                        elevation={3}
                        sx={{
                            borderRadius: { xs: 1, sm: 1.5, md: 2 },
                            overflow: 'hidden',
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 150
                                        }}>
                                            DOK Level
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 150
                                        }}>
                                            Criteria
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 120
                                        }}>
                                            Emerging
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 120
                                        }}>
                                            Developing
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 120
                                        }}>
                                            Proficient
                                        </TableCell>
                                        <TableCell sx={{
                                            fontWeight: "bold",
                                            color: theme.palette.primary.contrastText,
                                            minWidth: 120
                                        }}>
                                            Extending
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rubricData.rubric_rows?.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? "background.default" : "grey.50",
                                            }}
                                        >
                                            <TableCell sx={{
                                                fontWeight: 'medium',
                                                verticalAlign: 'top',
                                                minWidth: 150
                                            }}>
                                                {row.dok}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontWeight: 'medium',
                                                verticalAlign: 'top',
                                                minWidth: 150
                                            }}>
                                                {row.learning_outcome}
                                            </TableCell>
                                            <TableCell sx={{
                                                verticalAlign: 'top',
                                                minWidth: 120,
                                                fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                            }}>
                                                {row.emerging}
                                            </TableCell>
                                            <TableCell sx={{
                                                verticalAlign: 'top',
                                                minWidth: 120,
                                                fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                            }}>
                                                {row.developing}
                                            </TableCell>
                                            <TableCell sx={{
                                                verticalAlign: 'top',
                                                minWidth: 120,
                                                fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                            }}>
                                                {row.proficient}
                                            </TableCell>
                                            <TableCell sx={{
                                                verticalAlign: 'top',
                                                minWidth: 120,
                                                fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                            }}>
                                                {row.extending}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Paper>

                    {/* Additional Notes */}
                    {rubricData.notes && (
                        <Paper
                            elevation={2}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                mt: 4,
                                borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                border: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper
                            }}
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                                sx={{
                                    color: theme.palette.primary.main,
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <NoteIcon fontSize="small" />
                                Additional Notes
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    lineHeight: 1.6,
                                    fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                }}
                            >
                                {rubricData.notes}
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Fade>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 2, mt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>
                            <AssessmentIcon sx={{ fontSize: '2rem', color: theme.palette.primary.main }} />
                            <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: theme.palette.primary.main }}>
                                Rubric Generator
                            </Typography>
                        </Box>
                    </Box>

                    <Typography sx={{ fontSize: '1rem', fontStyle: 'italic', textAlign: 'center', mb: 4, color: theme.palette.text.secondary, lineHeight: 1.6, maxWidth: '800px', mx: 'auto' }}>
                        Build customized rubrics by selecting a subject, grade, and topic. Choose the DOK levels you want to assess, and generate clear criteria aligned to student learning outcomes.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label="Subject"
                                value={selectedSubject}
                                onChange={(e) => setSelectedSubject(e.target.value)}
                                placeholder="Enter subject name"
                                InputProps={{
                                    startAdornment: <SubjectIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                    }
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel>Grade</InputLabel>
                                <Select
                                    value={grade}
                                    label="Grade"
                                    onChange={(e) => setGrade(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GradeIcon fontSize="small" />
                                            Select a grade
                                        </Box>
                                    </MenuItem>
                                    {GRADES.map((g) => (
                                        <MenuItem key={g} value={g}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <GradeIcon fontSize="small" />
                                                Grade {g}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Topic"
                                fullWidth
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="e.g., Fractions on a Number Line"
                                InputProps={{
                                    startAdornment: <EmojiObjectsIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 }
                                    }
                                }}
                            />

                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <SpeedIcon fontSize="small" />
                                Select DOK Levels (1 – 4) you want included in the rubric:
                            </Typography>
                            <FormGroup row sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {DOK_OPTIONS.map((level) => (
                                    <FormControlLabel
                                        key={level}
                                        control={
                                            <Checkbox
                                                checked={dokLevels.includes(level)}
                                                onChange={() => handleDOKToggle(level)}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }}
                                            />
                                        }
                                        label={`DOK ${level}`}
                                        sx={{
                                            minWidth: 'fit-content',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                            }
                                        }}
                                    />
                                ))}
                            </FormGroup>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                size="large"
                                sx={{
                                    py: 1.5,
                                    borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.dark,
                                        transform: 'translateY(-2px)',
                                        boxShadow: 4
                                    }
                                }}
                            >
                                {loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Generating...</span>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <GenerateIcon />
                                        <span>Generate Rubric</span>
                                    </Stack>
                                )}
                            </Button>
                        </Stack>
                    </Paper>

                    {response && (
                        <Box sx={{ mt: 4 }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: 3,
                                flexDirection: isSmallMobile ? 'column' : 'row',
                                gap: 2
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleDownloadPDF}
                                    sx={{
                                        borderRadius: { xs: 1, sm: 1.5, md: 2 },
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download PDF</span>
                                    </Stack>
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => downloadWordDocument(response)}
                                    sx={{
                                        borderRadius: 2,
                                        backgroundColor: theme.palette.primary.main,
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.dark,
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DownloadIcon />
                                        <span>Download Word</span>
                                    </Stack>
                                </Button>
                            </Box>

                            <Paper
                                elevation={4}
                                sx={{
                                    p: { xs: 2, sm: 3, md: 4 },
                                    borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                    maxHeight: 600,
                                    overflowY: 'auto',
                                    background: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '@media print': {
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                        margin: 0,
                                        padding: '20px',
                                        maxHeight: 'none',
                                        overflowY: 'visible',
                                        pageBreakInside: 'avoid',
                                        breakInside: 'avoid'
                                    }
                                }}
                            >
                                {renderRubric(response)}
                            </Paper>
                        </Box>
                    )}

                    <Snackbar
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={() => setError('')}
                    >
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Snackbar>
                </Box>
            </Fade>

            {/* Loading State */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Creating comprehensive rubric for your assessment..."
                        />
                    </Paper>
                </Box>
            )}

            <AIGenerationLimitDialog
                open={showLimitDialog}
                onClose={() => setShowLimitDialog(false)}
                featureName="Rubric Generator"
                goToUpgrade={() => setActiveTabGlobal?.("Upgrade")}
            />
        </Container>
    );
};

export default RubricGenerator;



